import { useState } from 'react'
import { IconPasskey } from './icons'
import { passkeySupported, humanizePasskeyError } from '../lib/passkey'

// A passkey action button. The caller supplies `onActivate`, which runs the
// real WebAuthn ceremony (enrol or sign-in) and throws on failure; this button
// owns the busy/error UI and calls `onDone` on success.
export function PasskeyButton({
  label,
  onActivate,
  onDone,
  disabled,
  disabledHint,
}: {
  label: string
  onActivate: () => Promise<void>
  onDone?: () => void
  disabled?: boolean
  disabledHint?: string
}) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  async function go() {
    if (disabled || busy) return
    setBusy(true)
    setError('')
    try {
      await onActivate()
      onDone?.()
    } catch (e) {
      setError(humanizePasskeyError(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="passkey">
      <button
        type="button"
        className="passkey__btn"
        onClick={go}
        disabled={busy || disabled}
        aria-busy={busy}
      >
        {busy ? (
          <span className="passkey__spinner" aria-hidden />
        ) : (
          <span className="passkey__icon" aria-hidden>
            <IconPasskey size={20} />
          </span>
        )}
        <span className="passkey__label">{busy ? 'Waiting for passkey…' : label}</span>
      </button>
      {error ? (
        <span className="field__error" style={{ justifyContent: 'center' }}>
          {error}
        </span>
      ) : (
        <span className="passkey__hint">
          {disabled
            ? (disabledHint ?? 'Complete the step above first.')
            : passkeySupported()
              ? 'Fastest and most secure — no password to remember.'
              : 'Passwordless sign-in.'}
        </span>
      )}
    </div>
  )
}
